import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, ArrowRight, PlusCircle, MapPin, Upload, MessagesSquare, Waves, Store, BadgeCheck, Star } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import api, { fileUrl } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { CATEGORIES, ISLANDS, ISLAND_NAME } from "../lib/data";
import { CategoryIcon } from "../components/CategoryIcon";
import { ListingCard } from "../components/ListingCard";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";

const HERO_IMG = "https://images.unsplash.com/photo-1651570888736-126a600d4b29?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";

export default function Home() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [listings, setListings] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [counts, setCounts] = useState({});
  const [pros, setPros] = useState([]);

  useEffect(() => {
    api.get("/listings", { params: { sort: "recent", limit: 12 } }).then(({ data }) => {
      setListings(data);
      setFeatured(data.filter((l) => l.is_boosted).slice(0, 4));
    }).catch(() => {});
    api.get("/categories").then(({ data }) => setCounts(data)).catch(() => {});
    api.get("/professionals", { params: { limit: 12 } }).then(({ data }) => setPros(data)).catch(() => {});
  }, []);

  const submit = (e) => { e.preventDefault(); navigate(`/search?q=${encodeURIComponent(q)}`); };

  useSeo({
    title: lang === "it" ? "Canarias Market - Compra e vendi alle Isole Canarie"
      : lang === "en" ? "Canarias Market - Buy and sell in the Canary Islands"
      : "Canarias Market - Compra y vende en las Islas Canarias",
    description: lang === "it" ? "Il marketplace locale delle Isole Canarie: compra e vendi prodotti di seconda mano e scopri attività e servizi verificati."
      : lang === "en" ? "The local marketplace of the Canary Islands: buy and sell second-hand items and discover verified activities and services."
      : "El marketplace local de las Islas Canarias: compra y vende artículos de segunda mano y descubre actividades y servicios verificados.",
  });

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Canarias" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0F2537]/95 via-[#0F2537]/80 to-[#008080]/70" />
        </div>
        <div className="relative px-5 sm:px-8 py-14 sm:py-24 max-w-4xl">
          <span className="inline-flex items-center gap-2 font-mono-badge text-[11px] text-teal-200 bg-white/10 backdrop-blur px-3 py-1.5 rounded-full mb-5 cm-fade-up">
            <Waves className="h-3.5 w-3.5" /> ISLAS CANARIAS
          </span>
          <h1 className="font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] cm-fade-up" style={{ animationDelay: "60ms" }}>
            {t("home.title")}<br /><span className="text-[#FF9F1C]">{t("home.titleAccent")}</span>
          </h1>
          <p className="text-teal-100 text-base sm:text-lg mt-4 max-w-lg cm-fade-up" style={{ animationDelay: "120ms" }}>{t("home.subtitle")}</p>

          <form onSubmit={submit} className="mt-7 flex flex-col sm:flex-row gap-3 max-w-2xl cm-fade-up" style={{ animationDelay: "180ms" }}>
            <div className="flex-1 flex items-center gap-2 bg-white rounded-2xl sm:rounded-full px-5 h-14 shadow-xl">
              <Search className="h-5 w-5 text-slate-400 shrink-0" />
              <input data-testid="hero-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={t("home.searchPlaceholder")} className="flex-1 bg-transparent outline-none text-slate-900" />
              <button data-testid="hero-search-btn" type="submit" className="grid place-items-center h-10 px-4 sm:px-5 rounded-full bg-[#008080] hover:bg-[#006666] text-white font-semibold text-sm transition-colors shrink-0">{t("common.search")}</button>
            </div>
            <Link to="/sell" data-testid="hero-sell-btn" className="h-14 px-6 grid place-items-center rounded-2xl sm:rounded-full bg-[#FF9F1C] hover:bg-[#e88f13] text-white font-bold shadow-xl transition-colors">
              <span className="flex items-center gap-2"><PlusCircle className="h-5 w-5" /> {t("common.sellNow")}</span>
            </Link>
          </form>
          <p className="text-teal-200/70 text-xs mt-3 cm-fade-up" style={{ animationDelay: "240ms" }}>{t("home.searchExamples")}</p>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="px-5 sm:px-8 py-8">
        <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">{t("home.categories")}</h2>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          {CATEGORIES.map((c, i) => (
            <button key={c.id} data-testid={`category-${c.id}`} onClick={() => navigate(`/search?category=${c.id}`)}
              className="group flex flex-col items-center gap-2 p-4 rounded-2xl bg-white border border-slate-200 hover:border-teal-300 hover:shadow-md transition-all cm-fade-up" style={{ animationDelay: `${i * 25}ms` }}>
              <span className={`h-12 w-12 grid place-items-center rounded-xl ${c.color} group-hover:scale-110 transition-transform`}>
                <CategoryIcon name={c.icon} className="h-6 w-6" />
              </span>
              <span className="text-xs font-medium text-slate-700 text-center line-clamp-1">{t(`categories.${c.id}`)}</span>
              <span className="text-[10px] text-slate-400 font-mono-badge">{counts[c.id] || 0} {t("home.listingsCount")}</span>
            </button>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="px-5 sm:px-8 py-4">
          <h2 className="font-display font-bold text-2xl text-slate-900 mb-4 flex items-center gap-2">★ {t("home.featured")}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {featured.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
          </div>
        </section>
      )}

      {/* SERVICES CAROUSEL */}
      {pros.length > 0 && (
        <section className="px-5 sm:px-8 py-4" data-testid="home-services-carousel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display font-bold text-2xl text-slate-900 flex items-center gap-2"><Store className="h-6 w-6 text-[#008080]" />{t("services.title")}</h2>
              <p className="text-sm text-slate-500 mt-0.5">{t("services.ctaDesc")}</p>
            </div>
            <Link to="/servicios" data-testid="carousel-see-all" className="text-sm font-semibold text-[#008080] flex items-center gap-1 shrink-0">{t("home.seeAll")} <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2 snap-x">
            {pros.map((p) => (
              <Link key={p.id} to={`/user/${p.id}`} data-testid={`home-pro-${p.id}`}
                className="group snap-start shrink-0 w-64 bg-white border border-slate-200 rounded-3xl p-5 hover:border-teal-300 hover:shadow-lg transition-all">
                <div className="flex items-center gap-3">
                  <Avatar className="h-12 w-12 border border-slate-100">
                    <AvatarImage src={fileUrl(p.business_logo || p.profile_photo)} />
                    <AvatarFallback className="bg-teal-100 text-teal-700 font-bold">{(p.business_name || p.name || "?")[0]}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="font-display font-bold text-sm flex items-center gap-1 truncate">
                      <span className="truncate">{p.business_name || p.name}</span>
                      <BadgeCheck className="h-3.5 w-3.5 text-[#008080] shrink-0" />
                    </p>
                    {p.rating > 0 && <p className="text-[11px] text-slate-500 flex items-center gap-1"><Star className="h-3 w-3 fill-amber-400 text-amber-400" />{p.rating}</p>}
                  </div>
                </div>
                {p.business_category && (
                  <span className="mt-3 inline-block text-[11px] font-semibold text-[#008080] bg-teal-50 px-2.5 py-1 rounded-full">
                    {t(`monetization.business.cats.${p.business_category}`)}
                  </span>
                )}
                <p className="mt-3 text-sm text-slate-600 flex items-center gap-2 truncate">
                  <MapPin className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  <span className="truncate">{p.municipality || "—"}{p.island ? `, ${ISLAND_NAME[p.island]}` : ""}</span>
                </p>
                <span className="mt-3 text-sm text-[#008080] font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                  {t("services.viewProfile")} <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* RECENT */}
      <section className="px-5 sm:px-8 py-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-2xl text-slate-900">{t("home.recent")}</h2>
          <Link to="/search" data-testid="see-all-listings" className="text-sm font-semibold text-[#008080] flex items-center gap-1">{t("home.seeAll")} <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
        </div>
      </section>

      {/* ISLANDS */}
      <section className="px-5 sm:px-8 py-6">
        <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">{t("home.exploreIslands")}</h2>
        <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
          {ISLANDS.map((isl) => (
            <button key={isl.id} data-testid={`island-chip-${isl.id}`} onClick={() => navigate(`/search?island=${isl.id}`)}
              className="shrink-0 flex items-center gap-2 px-5 py-3 rounded-2xl bg-gradient-to-br from-[#008080] to-[#0F2537] text-white font-semibold text-sm hover:shadow-lg transition-shadow">
              <MapPin className="h-4 w-4" /> {isl.name}
            </button>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="px-5 sm:px-8 py-10">
        <h2 className="font-display font-bold text-2xl text-slate-900 mb-6 text-center">{t("home.howItWorks")}</h2>
        <div className="grid sm:grid-cols-3 gap-5 max-w-4xl mx-auto">
          {[
            { icon: Upload, title: t("home.step1"), desc: t("home.step1d") },
            { icon: MessagesSquare, title: t("home.step2"), desc: t("home.step2d") },
            { icon: MapPin, title: t("home.step3"), desc: t("home.step3d") },
          ].map((s, i) => (
            <div key={i} className="text-center p-6 rounded-2xl bg-white border border-slate-200">
              <span className="h-14 w-14 mx-auto grid place-items-center rounded-2xl bg-teal-50 text-[#008080] mb-4"><s.icon className="h-7 w-7" /></span>
              <h3 className="font-semibold text-slate-900">{s.title}</h3>
              <p className="text-sm text-slate-500 mt-1">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="px-5 sm:px-8 py-10 text-center text-slate-400 text-xs border-t border-slate-200">
        CANARIAS MARKET · El marketplace local de las Islas Canarias · © 2026
      </footer>
    </div>
  );
}
